import React from 'react';
import { AcademicCapIcon } from '@heroicons/react/24/outline';
import { Application } from '../../../types';

interface Props {
  application: Application;
  isEditing: boolean;
  onUpdate: (field: string, value: any) => void;
}

const EducationSection: React.FC<Props> = ({ application, isEditing, onUpdate }) => {
  const handleEduChange = (level: string, field: string, value: any) => {
    const edu = (application.education || {}) as any;
    onUpdate('education', {
      ...edu,
      [level]: {
        ...(edu[level] || {}),
        [field]: value
      }
    });
  };

  const renderEduField = (level: string, title: string, borderColor: string) => {
    const edu = (application.education || {}) as any;
    const data = edu[level];
    if (!data && !isEditing) return null;

    return (
      <div className={`border-l-4 ${borderColor} pl-4 space-y-2`}>
        <h4 className="font-medium text-gray-900">{title}</h4>
        {isEditing ? (
          <div className="grid grid-cols-2 gap-2">
            <input
              type="text"
              placeholder="Board/Degree"
              value={data?.board || data?.degree || ''}
              onChange={(e) => handleEduChange(level, level === 'graduation' ? 'degree' : 'board', e.target.value)}
              className="block w-full border border-gray-300 rounded-md shadow-sm p-1 text-sm"
            />
            <input
              type="text"
              placeholder="Year"
              value={data?.year || ''}
              onChange={(e) => handleEduChange(level, 'year', e.target.value)}
              className="block w-full border border-gray-300 rounded-md shadow-sm p-1 text-sm"
            />
            <input
              type="number"
              placeholder="Percentage"
              value={data?.percentage !== undefined && data?.percentage !== null ? data.percentage : ''}
              onChange={(e) => {
                const val = parseFloat(e.target.value);
                handleEduChange(level, 'percentage', isNaN(val) ? '' : val);
              }}
              className="block w-full border border-gray-300 rounded-md shadow-sm p-1 text-sm"
            />
            <input
              type="text"
              placeholder="School/College"
              value={data?.school || data?.college || ''}
              onChange={(e) => handleEduChange(level, level === 'tenth' || level === 'twelfth' ? 'school' : 'college', e.target.value)}
              className="block w-full border border-gray-300 rounded-md shadow-sm p-1 text-sm"
            />
          </div>
        ) : (
          <>
            <p className="text-sm text-gray-600">
              {data?.board || data?.degree} • {data?.year} • {data?.percentage}%
            </p>
            <p className="text-sm text-gray-600">{data?.school || data?.college}</p>
            {data?.university && <p className="text-sm text-gray-600">{data.university}</p>}
          </>
        )}
      </div>
    );
  };

  const hasAnyEducation = application.education && Object.values(application.education).some(v => !!v);

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
      <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
        <AcademicCapIcon className="w-5 h-5 mr-2 text-primary-600" />
        Education Details
      </h3>

      <div className="space-y-6">
        {renderEduField('tenth', '10th Standard', 'border-primary-500')}
        {renderEduField('twelfth', '12th Standard', 'border-blue-500')}
        {renderEduField('graduation', 'Graduation', 'border-green-500')}
        {renderEduField('bped', 'B.P.Ed', 'border-yellow-500')}

        {!isEditing && !hasAnyEducation && (
          <p className="text-sm text-gray-500 italic">No educational qualifications provided (Skipped during application).</p>
        )}
      </div>
    </div>
  );
};

export default EducationSection;
